import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, Award, TrendingUp, CheckCircle, Play, Download, Star, MessageSquare, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getToken } from '../services/tokenService';

export default function MesFormations() {
  const { user: user } = useAuth();
  const [inscriptions, setInscriptions] = useState([]);
  const [userCredits, setUserCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [evaluation, setEvaluation] = useState({ note: 5, commentaire: '' });

  useEffect(() => {
    loadFormations();
  }, []);

  const loadFormations = async () => {
    try {
      const token = getToken();
      
      // Charger les inscriptions
      const inscriptionsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formation/my-formations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const inscriptionsData = await inscriptionsRes.json();
      setInscriptions(inscriptionsData.inscriptions || []);

      // Charger les crédits
      const creditsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formation/my-credits`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const creditsData = await creditsRes.json();
      setUserCredits(creditsData);
    } catch {
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const updateProgression = async (inscriptionId, moduleId, progression) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formation/inscriptions/${inscriptionId}/progression`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ module_id: moduleId, progression }),
        }
      );

      if (!response.ok) throw new Error('Erreur mise à jour progression');

      toast.success('Progression mise à jour');
      loadFormations();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const genererAttestation = async (inscriptionId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formation/inscriptions/${inscriptionId}/attestation`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Erreur génération attestation');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_formation_${inscriptionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Attestation téléchargée avec succès');
    } catch {
      toast.error('Erreur lors de la génération de l\'attestation');
    }
  };

  const soumettreEvaluation = async (moduleId) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formation/evaluations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module_id: moduleId,
            note: evaluation.note,
            commentaire: evaluation.commentaire,
          }),
        }
      );

      if (!response.ok) throw new Error('Erreur soumission évaluation');

      toast.success('Évaluation enregistrée avec succès');
      setSelectedModule(null);
      setEvaluation({ note: 5, commentaire: '' });
    } catch {
      toast.error('Erreur lors de l\'enregistrement de l\'évaluation');
    }
  };

  const getStatutBadge = (statut) => {
    const statuts = {
      en_cours: { label: 'En cours', color: 'blue', icon: Play },
      termine: { label: 'Terminé', color: 'green', icon: CheckCircle },
      abandonne: { label: 'Abandonné', color: 'gray', icon: Clock },
    };
    const s = statuts[statut] || statuts.en_cours;
    const IconComponent = s.icon;
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-${s.color}-100 text-${s.color}-700`}>
        <IconComponent className="w-3 h-3" />
        {s.label}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const progressionTotale = inscriptions.length > 0
    ? inscriptions.reduce((sum, i) => {
        const progModules = Object.values(i.progression || {});
        const avgProg = progModules.length > 0
          ? progModules.reduce((a, b) => a + b, 0) / progModules.length
          : 0;
        return sum + avgProg;
      }, 0) / inscriptions.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 mb-1">Crédits disponibles</div>
                <div className="text-2xl font-bold text-purple-600">
                  {userCredits?.credits_disponibles || 0}
                </div>
              </div>
              <Award className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 mb-1">Formations en cours</div>
                <div className="text-2xl font-bold text-blue-600">
                  {inscriptions.filter((i) => i.statut === 'en_cours').length}
                </div>
              </div>
              <BookOpen className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 mb-1">Formations terminées</div>
                <div className="text-2xl font-bold text-green-600">
                  {inscriptions.filter((i) => i.statut === 'termine').length}
                </div>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 mb-1">Progression moyenne</div>
                <div className="text-2xl font-bold text-purple-600">
                  {Math.round(progressionTotale)}%
                </div>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des formations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-purple-600" />
            Mes Formations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inscriptions.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Vous n'avez pas encore de formations</p>
              <Button
                onClick={() => window.location.href = '/formascop'}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Découvrir le catalogue
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {inscriptions.map((inscription) => (
                <Card key={inscription.id} className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Parcours de formation
                          </h3>
                          {getStatutBadge(inscription.statut)}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Inscrit le {new Date(inscription.date_inscription).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {inscription.credits_consommes} crédits consommés
                        </div>
                      </div>
                      {inscription.statut === 'termine' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => genererAttestation(inscription.id)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Attestation
                        </Button>
                      )}
                    </div>

                    {/* Modules du parcours */}
                    <div className="space-y-3">
                      {inscription.modules?.map((module) => {
                        const progression = inscription.progression?.[module.id] || 0;
                        const estTermine = progression === 100;

                        return (
                          <div key={module.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{module.titre}</h4>
                                <div className="text-sm text-gray-600 mt-1">
                                  {module.duree_minutes} min • {module.credits_requis} crédits
                                </div>
                              </div>
                              {estTermine && (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              )}
                            </div>

                            {/* Barre de progression */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-gray-600">Progression</span>
                                <span className="font-semibold">{progression}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-purple-600 h-2 rounded-full transition-all"
                                  style={{ width: `${progression}%` }}
                                />
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              {!estTermine && inscription.statut === 'en_cours' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateProgression(inscription.id, module.id, Math.min(progression + 25, 100))}
                                  >
                                    Avancer (+25%)
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateProgression(inscription.id, module.id, 100)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Terminer
                                  </Button>
                                </>
                              )}
                              {estTermine && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setSelectedModule(module)}
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Évaluer
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Évaluation */}
      {selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-500" />
                Évaluer la formation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{selectedModule.titre}</h4>
                <p className="text-sm text-gray-600">{selectedModule.description}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note (1-5 étoiles)
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((note) => (
                    <button
                      key={note}
                      onClick={() => setEvaluation({ ...evaluation, note })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          note <= evaluation.note
                            ? 'text-yellow-500 fill-yellow-500'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  value={evaluation.commentaire}
                  onChange={(e) => setEvaluation({ ...evaluation, commentaire: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  placeholder="Partagez votre expérience..."
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSelectedModule(null)}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={() => soumettreEvaluation(selectedModule.id)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Envoyer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
